import {
  getRequest,
  putRequest,
  postRequest,
  deleteRequest,
} from "../../../../../../api/http-v2";

/**
 * Fetches all images for a specific listing.
 * @param {string} listingId The ID of the listing.
 * @returns {Promise<object>} A promise that resolves with the listing image data.
 */
export async function getListingImages(listingId) {
  const endpoint = `/v2/spaces-v2/listings/${listingId}`;
  const query = {
    subresources: ["images", "embed-media"],
  };

  const response = await getRequest({
    endpoint,
    query,
    options: { useAccessToken: true },
  });
  return response.data;
}

/**
 * Fetches the embedded media, prices and inventory for a specific listing.
 * @param {string} listingId The ID of the listing.
 * @returns {Promise<object>} A promise that resolves with the listing media, price and inventory data.
 */
export async function getListingMediaPriceAndInventory(listingId) {
  const endpoint = `/v2/spaces-v2/listings/${listingId}`;
  const query = {
    subresources: ["embed-media", "prices", "inventory"],
  };

  const response = await getRequest({
    endpoint,
    query,
    options: { useAccessToken: true },
  });
  return response.data;
}

/**
 * Creates a new listing image.
 * @param {object} newListingImage The data for the new image.
 * @returns {Promise<object>} A promise that resolves with the newly created image data.
 */
export async function createListingImage(newListingImage) {
  const endpoint = "/v1/listings/images/";
  const body = newListingImage;
  const query = {
    timestamp: Date.now(),
  };

  return postRequest({
    endpoint,
    body,
    query,
    options: { useAccessToken: true },
  });
}

/**
 * Updates an existing listing image.
 * @param {object} image An object containing the image ID and the payload for the update.
 * @returns {Promise<object>} A promise that resolves with the updated image data.
 */
export async function updateListingImage(image) {
  const endpoint = `/v1/listings/images/${image.id}`;
  const body = image.payload;

  return putRequest({ endpoint, body, options: { useAccessToken: true } });
}

/**
 * Deletes a specific listing image.
 * @param {string} listingImageId The ID of the image to delete.
 * @returns {Promise<object>} A promise that resolves with the API response, often a success message.
 */
export async function deleteListingImage(listingImageId) {
  const endpoint = `/v1/listings/images/${listingImageId}`;

  return deleteRequest({ endpoint, options: { useAccessToken: true } });
}

// Listing Video Embeds API Calls (For Editing a Listing)

/**
 * Creates new video/3D tour records associated with a Listing.
 * @param {string} listingId The ID of the listing.
 * @param {Array<object>} mediaPayload Array of objects: [{ url, sort_order }]
 * @returns {Promise<object>} A promise that resolves with the created media objects (including IDs).
 */
export async function createListingEmbedMedia(listingId, mediaPayload) {
  const endpoint = `/v2/spaces-v2/listings/${listingId}/embed-media`;
  const response = await postRequest({
    endpoint,
    body: mediaPayload,
    options: { useAccessToken: true },
  });
  return response.data;
}

/**
 * Updates the sort order of existing video/3D tour records on a Listing.
 * @param {string} listingId The ID of the listing.
 * @param {Array<object>} orderPayload Array of objects: [{ id, sort_order }]
 * @returns {Promise<object>} A promise that resolves with the updated media objects.
 */
export async function updateListingEmbedMediaOrder(listingId, orderPayload) {
  const endpoint = `/v2/spaces-v2/listings/${listingId}/embed-media`;
  return putRequest({
    endpoint,
    body: orderPayload,
    options: { useAccessToken: true },
  });
}

/**
 * Deletes a specific video/3D tour record from a Listing.
 * @param {string} listingId The ID of the listing.
 * @param {string} mediaId The ID of the video/3D tour record to delete.
 * @returns {Promise<object>} A promise that resolves upon successful deletion.
 */
export async function deleteListingEmbedMedia(listingId, mediaId) {
  const endpoint = `/v2/spaces-v2/listings/${listingId}/embed-media/${mediaId}`;
  return deleteRequest({ endpoint, options: { useAccessToken: true } });
}
